import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import Logo from "../components/Logo";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === "kunde") navigate("/portal");
      else if (user.role === "admin") navigate("/admin");
      else if (user.role === "service") navigate("/service");
      else if (user.role === "vask") navigate("/vask");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left – form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#F8F9FA]">
        <div className="w-full max-w-md">
          <Logo />
          <h1 className="mt-10 text-3xl sm:text-4xl font-bold tracking-tight text-[#0C1824]">
            Velkommen tilbake
          </h1>
          <p className="mt-2 text-sm text-[#2B3F4D]">
            Logg inn for å administrere flåten eller se din booking.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Passord</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full bg-[#BD984D] hover:bg-[#A57F39] text-white h-11"
            >
              {loading ? "Logger inn…" : "Logg inn"}
            </Button>
          </form>

          <div className="mt-6 rounded-lg border border-[#BD984D]/40 bg-[#BD984D]/5 p-4">
            <div className="text-xs uppercase tracking-[0.24em] text-[#A57F39] font-semibold">
              For besøkende
            </div>
            <p className="text-sm text-[#0C1824] mt-1">
              Se dagens flåte, ledighet og gode tilbud – uten å logge inn.
            </p>
            <Link to="/tilgjengelighet">
              <Button
                type="button"
                variant="outline"
                data-testid="public-availability-link"
                className="mt-3 w-full border-[#BD984D] text-[#A57F39] hover:bg-[#BD984D]/10"
              >
                Sjekk tilgjengelighet · Check availability
              </Button>
            </Link>
          </div>

          <div className="mt-3 rounded-lg border border-[#1DA3B3]/40 bg-[#1DA3B3]/5 p-4">
            <div className="text-xs uppercase tracking-[0.24em] text-[#1DA3B3] font-semibold">
              Ny tjeneste
            </div>
            <p className="text-sm text-[#0C1824] mt-1">
              Løft av privat båt opptil 15 tonn – bestill tid og betal på nett.
            </p>
            <Link to="/loft">
              <Button
                type="button"
                variant="outline"
                data-testid="public-lift-link"
                className="mt-3 w-full border-[#1DA3B3] text-[#1DA3B3] hover:bg-[#1DA3B3]/10"
              >
                Book båtløft
              </Button>
            </Link>
          </div>

          <p className="text-xs text-[#2B3F4D] mt-6">
            Har du fått en invitasjon?{" "}
            <Link to="/accept-invite" className="text-[#A57F39] underline">
              Aktiver konto
            </Link>
          </p>
          <p className="text-xs text-[#2B3F4D] mt-3">
            <Link to="/personvern" className="text-[#A57F39] underline">
              Personvernerklæring
            </Link>{" "}
            ·{" "}
            <Link to="/privacy" className="text-[#A57F39] underline">
              Privacy policy
            </Link>
          </p>
        </div>
      </div>
      {/* Right – brand panel */}
      <div className="hidden md:block flex-1 relative">
        <img
          src="/boat-39-side.jpg"
          className="absolute inset-0 w-full h-full object-cover"
          alt=""
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0C1824]/85 via-[#0C1824]/65 to-[#0C1824]/30" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Logo dark withText size={48} />
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-[#ECC974]">
              Eco-Friendly Boat Rental
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mt-2 leading-tight">
              Elektriske båter <br /> på Telemarkskanalen.
            </h2>
            <p className="mt-3 text-white/70 max-w-md">
              Flåteadministrasjon og kundeportal i én plattform.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
